import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';
import { Box, Typography } from '@mui/material';
import './style.css';


interface MarkdownViewerProps {
  content: string;
}

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ content }) => {
  const components: Components = {
          h1: ({ node, ...props }) => (
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#3c4043', mb: 2 }} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#3c4043', mb: 2, mt: 3 }} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#3c4043', mb: 1.5, mt: 2.5 }} {...props} />
          ),
          p: ({ node, ...props }) => (
            <Typography variant="body1" paragraph sx={{ color: '#5f6368', mb: 2 }} {...props} />
          ),
          ul: ({ node, ...props }) => (
            <Box component="ul" sx={{ pl: 2, mb: 2 }} {...props} />
          ),
          li: ({ node, ...props }) => (
            <Typography component="li" variant="body1" sx={{ color: '#5f6368', mb: 1 }} {...props} />
          ),
          a: ({ node, ...props }) => (
            <Typography
              component="a"
              sx={{
                color: '#1a73e8',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              {...props}
            />
          ),
          table: ({node, ...props}) => (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', my: 2, background: '#fff' }} {...props} />
          ),
          th: ({node, ...props}) => (
            <Box component="th" sx={{ border: '1px solid #dadce0', p: 1, background: '#f5f5f5', fontWeight: 600, textAlign: 'center' }} {...props} />
          ),
          td: ({node, ...props}) => (
            <Box component="td" sx={{ border: '1px solid #dadce0', p: 1, textAlign: 'center' }} {...props} />
          ),
          code: (props) => {
            const {children, className, node, ...rest} = props;
            const match = /language-(\w+)/.exec(className || '');
            return match ? (
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f6f8fa',
                  color: '#24292e',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '14px',
                  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                  border: '1px solid #e1e4e8',
                  my: 2
                }}
              >
                <Typography
                  component="code"
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    color: 'inherit'
                  }}
                  {...rest}
                >
                  {String(children).replace(/\n$/, '')}
                </Typography>
              </Box>
            ) : (
              <Typography
                component="code"
                className="inline-code" // 여기서 className을 사용하여 스타일을 적용합니다.
                {...rest}
              >
                {children}
              </Typography>
            );
          },
          pre: ({ node, ...props }) => (
            <Box component="pre" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <Box
              component="blockquote"
              sx={{
                borderLeft: '4px solid #dfe2e5',
                paddingLeft: 2,
                margin: '16px 0',
                color: '#6a737d',
                backgroundColor: '#f6f8fa',
                padding: 2,
                borderRadius: 1
              }}
              {...props}
            />
          ),
        };

  return (
    <Box sx={{ p: 2 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownViewer;
